import { createAsyncThunk } from "@reduxjs/toolkit"
import customFetch from "../../util/config/axiosConfig"
import { getDataUserId } from "../../util/manageAuth"
import { ChatbotSearchData, UploadRuleParams, UploadRuleResponse } from "../../types/chatbotType"
import { ApiResponse } from "../../types/responseType"

export const uploadChatbotRule = createAsyncThunk<
    UploadRuleResponse,
    UploadRuleParams,
    { rejectValue: string }
>(
    "chatbot/uploadRule",
    async (params, thunkAPI) => {
        try {
            const userId = getDataUserId()
            if (!userId) throw new Error("User not authenticated")

            const queryParams = new URLSearchParams({
                userId: String(userId),
                keyword: params.keyword.trim(),
                responseText: params.responseText.trim(),
                notifyAdmin: String(params.notifyAdmin ?? 0),
            })

            if (params.notifyAdmin === 1) {
                if (params.notifyMobile) queryParams.set("notifyMobile", params.notifyMobile.trim())
                if (params.notifyMessage) queryParams.set("notifyMessage", params.notifyMessage.trim())
            }

            let body: FormData | string
            let headers: Record<string, string> = {}

            if (params.file) {
                const formData = new FormData()
                formData.append("file", params.file)
                body = formData
            } else {
                body = JSON.stringify({ file: "" })
                headers["Content-Type"] = "application/json"
            }

            const response = await customFetch.post(
                `/api/chatbot/upload-rule?${queryParams.toString()}`,
                body,
                { headers }
            )

            return response.data

        } catch (error: any) {
            return thunkAPI.rejectWithValue(
                error?.message || "Failed to upload chatbot rule"
            )
        }
    }
)

export const fetchChatbotRules = createAsyncThunk<
    ChatbotSearchData,
    { page?: number; size?: number; userId?: string },
    { rejectValue: string }
>(
    "chatbot/fetchRules",
    async ({ page = 0, size = 10, userId }, thunkAPI) => {
        try {
            const finalUserId = userId || getDataUserId()
            if (!finalUserId) throw new Error("User not authenticated")

            const queryParams = new URLSearchParams({
                page: String(page),
                size: String(size),
                userId: String(finalUserId),
            })

            const response = await customFetch.get<ApiResponse<ChatbotSearchData>>(
                `/api/chatbot/search?${queryParams.toString()}`
            )
            return response.data.data
        } catch (error: any) {
            return thunkAPI.rejectWithValue(
                error?.message || "Failed to fetch chatbot rules"
            )
        }
    }
)

export const updateChatbotRule = createAsyncThunk<
    UploadRuleResponse,
    {
        id: number
        keyword: string
        responseText: string
        responseType: string
        mediaId?: string
        notifyAdmin?: 0 | 1
        notifyMobile?: string
        notifyMessage?: string
    },
    { rejectValue: string }
>(
    "chatbot/updateRule",
    async (params, thunkAPI) => {
        try {
            const userId = getDataUserId()
            if (!userId) throw new Error("User not authenticated")

            const response = await customFetch.put(
                `/api/chatbot/whatsapp/chatbot-rule/update`,
                {
                    id: params.id,
                    userId,
                    keyword: params.keyword.trim(),
                    responseText: params.responseText.trim(),
                    responseType: params.responseType,
                    mediaId: params.mediaId ?? "",
                    notifyAdmin: params.notifyAdmin ?? 0,
                    notifyMobile: params.notifyAdmin === 1 ? (params.notifyMobile?.trim() ?? "") : "",
                    notifyMessage: params.notifyAdmin === 1 ? (params.notifyMessage?.trim() ?? "") : "",
                }
            )

            return response.data
        } catch (error: any) {
            return thunkAPI.rejectWithValue(
                error?.message || "Failed to update chatbot rule"
            )
        }
    }
)
